import React from "react"
import { connect } from "react-redux"
import styled, { css } from "styled-components"
import { fontSize, colors } from "buoy-theme"
import { RoundButton } from "buoy-core"
import Modal from "../common/components/Modal"
import Link from "../common/components/Link"
import Text from "../common/components/Text"
import tracker from "../common/tracker"
import { FormattedMessage } from "react-intl"

import { setShowGinaModal } from "../interview/actions"
import { questionAfterTerms } from "../identifiers/actions"
import { setTermsAgreed } from "../embed/actions"
import { SYMPTOMS_URL, TERMS_URL, PRIVACY_URL } from "../common/constants"

const GinaBodyWrapper = styled.div`
  align-items: center;
  display: inline;
  padding: 18px 0px 0px;
`

const GinaText = styled(Text)`
  ${props =>
    props.showFirstPage ? fontSize("bodySmall") : fontSize("bodyMain")};
  text-align: left;
  display: block;
  width: 100%;
  margin-bottom: ${props => (props.lastText ? "" : "16px;")};
  // On very small screen sizes adjust font and margin
  @media (max-width: 380px) {
    font-size: 11px;
    margin-bottom: 10px;
  }
  ${props =>
    props.showFirstPage && props.showCovid19AlertBanner &&
    // specifically for the banner/gina overlap
    css`
      @media (max-width: 380px) {
        font-size: 11px;
        margin-bottom: 10px;
      }
    `}
`

const GinaLinkWrapper = styled.div`
  text-align: center;
`
const GinaLink = styled(Link)`
  ${fontSize("bodyMain")}
`

const GinaButton = styled(RoundButton)`
  display: block;
  width: 100%;
  @media (min-width: 380px) {
    margin: 32px 0px 16px;
  }
  @media (max-width: 380px) {
    margin: 18px 0px 18px;
  }
`

const Legal = styled.div`
  position: absolute;
  display: block;
  left: 0;
  right: 0;
  bottom: 0;
  ${fontSize("bodySmall")}
  color: ${colors.neutralDark};
  text-align: center;
  width: 55%;
  margin: auto;
  bottom: 44px;
  @media (max-width: 640px) {
    bottom: 28px;
  }
  @media (max-width: 320px) {
    width: 68%
  }
  @media (max-width: 375px) and (min-width: 321px) {
    width: 63%
  }

`

const LegalLink = styled(Link)`
  color: ${colors.neutralDark};
`

const GinaFirstPage = ({ acceptTerms, action, showFirstPage, showCovid19AlertBanner }) => (
  <GinaBodyWrapper>
    <GinaText showFirstPage={showFirstPage}>
      <FormattedMessage
        id="interview.modals.gina.text"
        defaultMessage={`Buoy Health’s services are not intended to be a substitute for professional medical advice, diagnosis, or treatment. Your use of the services is voluntary and subject to Buoy Health’s <terms>Terms of Use</terms> and <privacy>Privacy Policy</privacy>.`}
        values={{
          terms: (...chunks) => (
            <Link
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              size="bodySmall"
              ginaModal={true}
              showCovid19AlertBanner={showCovid19AlertBanner}
            >
              {chunks}
            </Link>
          ),
          privacy: (...chunks) => (
            <Link
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              size="bodySmall"
              ginaModal={true}
              showCovid19AlertBanner={showCovid19AlertBanner}
            >
              {chunks}
            </Link>
          ),
        }}
      />
    </GinaText>
    <GinaText showFirstPage={showFirstPage} showCovid19AlertBanner={showCovid19AlertBanner}>
      <FormattedMessage
        id="interview.modals.gina.text.second"
        defaultMessage="By clicking “Agree & Continue” you hereby authorize Buoy Health to ask you about your symptoms, health status, and behaviors. Questions may relate to diseases, illnesses, or conditions you may have or that may run in your family. Your answers will help us provide you with medical information and identify services that may be relevant to your health."
      />
    </GinaText>
    <GinaText showFirstPage={showFirstPage} showCovid19AlertBanner={showCovid19AlertBanner} lastText={true}>
      <FormattedMessage
        id="interview.modals.gina.text.third"
        defaultMessage="Buoy Health uses reasonable physical, technical, and administrative safeguards (such as firewalls, encryption, identity management, and intrusion prevention and detection) to protect your information. We will not share your information with third parties unless you give your consent or unless permitted by applicable law."
      />
    </GinaText>
    <GinaLinkWrapper showFirstPage={showFirstPage}>
      <GinaButton onClick={acceptTerms} showFirstPage={showFirstPage}>
        <FormattedMessage
          id="interview.modals.gina.confirm"
          defaultMessage="Agree & Continue"
        />
      </GinaButton>
    </GinaLinkWrapper>

    <GinaLinkWrapper>
      <GinaLink onClick={() => action(false)}>
        <FormattedMessage
          id="interview.modals.gina.deny"
          defaultMessage="I do not agree"
        />
      </GinaLink>
    </GinaLinkWrapper>
  </GinaBodyWrapper>
)

const GinaSecondPage = ({ action, showFirstPage }) => (
  <GinaBodyWrapper>
    <GinaText showFirstPage={showFirstPage} lastText={true}>
      <FormattedMessage
        id="interview.modals.gina.postmessage"
        defaultMessage="But you still have options. Be sure to check our symptom library – it’s
        another great resource for health questions."
      />
    </GinaText>
    <GinaButton
      showFirstPage={showFirstPage}
      onClick={() => window.open(SYMPTOMS_URL)}
    >
      <FormattedMessage
        id="interview.modals.gina.postmessage.button"
        defaultMessage="Go to symptom library"
      />
    </GinaButton>
    <GinaLinkWrapper>
      <GinaLink onClick={() => action(true)}>
        <FormattedMessage
          id="interview.modals.gina.postmessage.back"
          defaultMessage="Back"
        />
      </GinaLink>
    </GinaLinkWrapper>
    <Legal>
      <FormattedMessage
        id="interview.modals.gina.postmessage.legal"
        defaultMessage="Learn more about our <terms>Terms of Use</terms> and <privacy>Privacy Notice</privacy>."
        values={{
          terms: (...chunks) => (
            <LegalLink
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {chunks}
            </LegalLink>
          ),
          privacy: (...chunks) => (
            <LegalLink
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {chunks}
            </LegalLink>
          ),
        }}
      />
    </Legal>
  </GinaBodyWrapper>
)

class GinaModal extends React.Component {
  constructor(props) {
    super(props)
    this.state = { showFirstPage: true }
    this.handler = this.handler.bind(this)
  }

  handler(showFirstPage) {
    this.setState({ showFirstPage: showFirstPage })
  }

  render() {
    return this.props.show ? (
      <Modal
        ginaModal={true}
        showFirstPage={this.state.showFirstPage}
        heading={
          this.state.showFirstPage ? (
            ""
          ) : (
            <FormattedMessage
              id="interview.modals.gina.postmessage.header"
              defaultMessage="Your consent is required to use Buoy Assistant."
            />
          )
        }
        showCancel={false}
        showSubmit={false}
        extraButtons={false}
        cancelClick={() => {}}
        showCovid19AlertBanner={this.props.showCovid19AlertBanner}
      >
        {this.state.showFirstPage ? (
          <GinaFirstPage
            acceptTerms={() =>
              this.props.acceptTerms(this.props.simpleFlowDiagnosis)
            }
            action={this.handler}
            showFirstPage={this.state.showFirstPage}
            showCovid19AlertBanner={this.props.showCovid19AlertBanner}
          />
        ) : (
          <GinaSecondPage
            action={this.handler}
            showFirstPage={this.state.showFirstPage}
          />
        )}
      </Modal>
    ) : (
      <span />
    )
  }
}

const mapStateToProps = state => ({
  show: state.modals.showGinaModal,
  visitChat: state.brand.visit_chat,
  brand: state.brand.name,
  simpleFlowDiagnosis: state.simpleFlow.diagnosis,
  showCovid19AlertBanner: state.showCovid19AlertBanner
})

const mapDispatchToProps = (dispatch, ownProps) => ({
  acceptTerms: simpleFlowDiagnosis => {
    dispatch(setTermsAgreed(true))
    dispatch(setShowGinaModal(false))
    dispatch(questionAfterTerms())
    tracker.acceptGinaTerms()
  },
})

const GinaModalWrapper = connect(mapStateToProps, mapDispatchToProps)(GinaModal)

export { GinaModalWrapper as default, GinaModal }



// WEBPACK FOOTER //
// ./assets/modals/GinaModal.js